import { IBaseResponse, fetchWrapper, extractPaginationInfo, getRequestHeaders, apiClient } from './apiBase';
import { IPagination, IRepository, ICommit } from '@/types';
import { BASE_API_URL } from '@/config/environment';

export interface IRepositoryBranch {
  // commit_id: string;
  is_head?: boolean;
  is_collaboration_enabled?: boolean;
  name: string;
  commit: ICommit;
}

interface IRepositoryBranchResponse extends IBaseResponse {
  branch?: IRepositoryBranch;
  is_cacher_pending?: boolean;
}

export interface IRepositoryBranchesResponse extends IBaseResponse {
  branches?: IRepositoryBranch[];
  pagination?: IPagination;
}

const branchesResponseHandler = (data: IRepositoryBranchesResponse): IRepositoryBranchesResponse => {
  const { branches, status, status_message, error } = data;
  const dateSortedBranches = branches?.sort((a, b) =>
    (b.commit?.timestamp as string).localeCompare(a.commit?.timestamp as string),
  );
  const pagination = extractPaginationInfo(data);
  return {
    status,
    status_message,
    branches: dateSortedBranches?.sort((a, b) => (!!a.is_head > !!b.is_head ? -1 : 1)),
    pagination,
    error: error || null,
  };
};

const branchResponseHandler = (data: IRepositoryBranchResponse): IRepositoryBranchResponse => {
  const { branch, status, status_message, error, is_cacher_pending } = data;
  return {
    status,
    status_message,
    branch,
    error: error || null,
    is_cacher_pending,
  };
};

export const fetchRepositoryBranch = async (
  namespace: string,
  repositoryName: string,
  branchName: string,
  token?: string,
): Promise<IRepositoryBranchResponse> => {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/branches/${branchName}`;
  // console.log('fetchRepositoryBranch', targetUrl);

  const data = await fetchWrapper({
    resourceName: 'repositoryBranch',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName, branchName },
  });

  return branchResponseHandler(data);
};

// export const fetchRepositoryBranchCollaborationStatus = async (
//   namespace: string,
//   repositoryName: string,
//   branchName: string,
//   token?: string,
// ): Promise<IRepositoryBranchesResponse> => {
//   const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/branches/get_collaboration_status/${branchName}`;
//   // console.log('fetchRepositoryBranchCollaborationStatus', targetUrl);
//   const data = await fetchWrapper({
//     resourceName: 'repositoryBrancheCollaborationStatus',
//     targetUrl,
//     authToken: token,
//     resourceContext: { namespace, repositoryName, branchName },
//   });

//   return branchResponseHandler(data);
// };

export const fetchRepositoryBranches = async (
  namespace: string,
  repositoryName: string,
  token?: string,
): Promise<IRepositoryBranchesResponse> => {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/branches`;

  const data = await fetchWrapper({
    resourceName: 'repositoryBranches',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
  });

  return branchesResponseHandler(data);
};

// interface ISetBranchCollaborationParams {
//   namespace: string;
//   repositoryName: string;
//   branchName: string;
//   is_collaboration_enabled: boolean;
//   token?: string;
// }

// export const setBranchCollaborationStatus = async ({
//   namespace,
//   repositoryName,
//   branchName,
//   is_collaboration_enabled,
//   token,
// }: ISetBranchCollaborationParams): Promise<IRepositoryBranchResponse> => {
//   const requestHeaders = getRequestHeaders(token);
//   const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/branches/set_collaboration_status/${branchName}`;
//   try {
//     const res = await apiClient.post(
//       targetUrl,
//       { is_collaboration_enabled: is_collaboration_enabled },
//       { headers: requestHeaders },
//     );
//     return res.data;
//   } catch (error: any) {
//     return error.response.data;
//   }
// };

type TResourceBranch = {
  name: string;
  is_head: boolean;
  commit_id: string;
  is_collaboration_enabled?: boolean;
};

export type IResourceDefinition = {
  path: string;
  version: string;
  branch: TResourceBranch | null;
  commit: ICommit | null;
};

const emptyResourceDefinition = {
  path: '',
  version: '',
  branch: null,
  commit: null,
};

export const fetchResourceDefiniton = async (
  repository: IRepository,
  filePath: string[],
  token?: string,
): Promise<IResourceDefinition> => {
  if (repository.latest_commit === null) {
    return emptyResourceDefinition;
  }
  const path = filePath ? filePath.join('/') : repository.default_branch;
  const targetUrl = `${BASE_API_URL}/repos/${repository.namespace}/${repository.name}/revisions/${path}`;
  // console.log('fetchResourceDefiniton', targetUrl);
  const data = await fetchWrapper({
    resourceName: 'resourceDefinition',
    targetUrl,
    authToken: token,
    resourceContext: { namespace: repository.namespace, repositoryName: repository.name },
  });
  return {
    path: data?.resource?.path,
    version: data?.resource?.version,
    branch: data?.resource?.branch,
    commit: data?.resource?.commit,
  };
};

// export const deleteBranch = async ({
//   compareId,
//   namespace,
//   repositoryName,
//   token,
// }: IDeleteCompareParams): Promise<ICompareResponse> => {
//   const requestHeaders = getRequestHeaders(token);
//   const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/compares/${compareId}`;
//   try {
//     const res = await apiClient.delete(targetUrl, { headers: requestHeaders });
//     return res.data;
//   } catch (error: any) {
//     return error.response.data;
//   }
// };

interface ICreateBranchParams {
  namespace: string;
  repositoryName: string;
  token?: string;
  new_name: string;
  from_name: string;
}

export const createBranch = async ({
  namespace,
  repositoryName,
  token,
  from_name,
  new_name,
}: ICreateBranchParams): Promise<IRepositoryBranchResponse> => {
  const targetUrl = `${BASE_API_URL}/repos/${namespace}/${repositoryName}/branches`;

  const data = await fetchWrapper({
    resourceName: 'createBranch',
    method: 'POST',
    targetUrl,
    authToken: token,
    resourceContext: { namespace, repositoryName },
    body: JSON.stringify({ from_name, new_name }),
  });

  return data;
};
